import { useOpenCalendarTab } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/useOpenCalendarTab';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { emailRegexp } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import React, { useCallback } from 'react';
import { getFormattedTime } from '@lmt/unicorn-shared/app/components/patients/common';
import { AppointmentStatusCodes, QueryFactory } from '@lmt/shared-types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import { MoreContactsPopup } from './MoreContactsPopup';
import SaveIcon from '@lmt/unicorn-shared/app/icons/small-tick.svg?react';
import CancelIcon from '@lmt/unicorn-shared/app/icons/red-cross.svg?react';

import styles from './PlannedVisitsPanel.module.scss';
import { VisitStatusOption } from '../../patients/profile/cases/components/case-tree/VisitStatusOption';
import { UniLink } from '../../common/ui-kit/buttons/UniLink';
import { ButtonColor } from '../../common/ui-kit/buttons/Button';

export interface PlannedVisitInfo {
  id: number;
  startDate: Date;
  patientId: number;
  patientName: string;
  subtenantCode: string;
  patientContacts: string[];
  priorityContact: string;
  visitType: string;
  statusId: AppointmentStatusCodes;
  caseId: number;
}

export interface Props {
  visit: PlannedVisitInfo;
  openPatientTab: (patientId: number) => void;
  openCaseVisitTab: (
    patientId: number,
    caseId: number,
    visitId: number,
  ) => void;
}

export const PlannedVisitItem: React.FC<Props> = (props) => {
  const tabs = useTabs();
  const modals = useModal();
  const calendarSettingsQuery =
    QueryFactory.CalendarSettingsQuery.useGetQuery();
  const openCalendarTab = useOpenCalendarTab(
    modals,
    undefined,
    tabs,
    calendarSettingsQuery.data?.defaultCalendarView,
  );
  const { visit, openPatientTab, openCaseVisitTab } = props;
  const i18next = useScopedTranslation('dashboard.planned_visits');
  const dispatch = useUniDispatch();

  const cancelOrConfirmVisit = useCallback(
    async (status: AppointmentStatusCodes) => {
      if (
        !(
          status === AppointmentStatusCodes.Canceled ||
          status === AppointmentStatusCodes.Confirmed
        )
      ) {
        return;
      }
      const isCancel = status === AppointmentStatusCodes.Canceled;
      const isConfirmed = await modals.showConfirm(
        isCancel
          ? {
              title: i18next.t('confirmation_cancel_visit_title'),
              text: i18next.t('confirmation_cancel_visit_text'),
              okButtonText: i18next.t('confirmation_cancel_visit_button'),
            }
          : {
              title: i18next.t('confirmation_confirm_visit_title'),
              text: i18next.t('confirmation_confirm_visit_text'),
              okButtonText: i18next.t('confirmation_confirm_visit_button'),
              okButtonColor: ButtonColor.Secondary,
            },
      );

      if (isConfirmed) {
        try {
          if (isCancel) {
            await QueryFactory.AppointmentEventsClient.cancelVisit(visit.id);
            await openCalendarTab(visit.startDate);
          } else {
            await QueryFactory.AppointmentEventsClient.confirmVisit(visit.id);
          }
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }
    },
    [openCalendarTab],
  );

  return (
    <div
      className={styles.visit}
      data-disabled={visit.statusId === AppointmentStatusCodes.Canceled}
      onClick={() => openCaseVisitTab(visit.patientId, visit.caseId, visit.id)}
      data-test-id="planned-visits-item"
    >
      <div className={styles.row}>
        <div className={styles.timeRange}>
          {getFormattedTime(visit.startDate)}
        </div>
        <div className={styles.visitInfoColumn}>
          <div className={styles.patientContainer}>
            <div
              className={styles.patient}
              onClick={(e) => {
                e.stopPropagation();
                openPatientTab(visit.patientId);
              }}
            >
              <div title={visit.patientName} className={styles['patient-name']}>
                {visit.patientName}
              </div>
            </div>
            <div className={styles.subtenantCode}>{visit.subtenantCode}</div>
          </div>
          <div className={styles.visitType}>{visit.visitType}</div>
        </div>
        <div className={styles.visitInfoColumn}>
          <div className={styles.firstContact}>
            {visit.priorityContact.match(emailRegexp) ? (
              <UniLink
                title={visit.priorityContact}
                color={ButtonColor.Primary}
                href={`mailto:${visit.priorityContact}`}
              />
            ) : (
              <span>{visit.priorityContact}</span>
            )}
          </div>
          {visit.patientContacts.length > 1 && (
            <MoreContactsPopup
              title={i18next.t('more_contacts')}
              contacts={visit.patientContacts.filter(
                (contact) => contact !== visit.priorityContact,
              )}
            />
          )}
        </div>
      </div>
      <div
        className={styles.statusContainer}
        onClick={(e) => e.stopPropagation()}
      >
        {(visit.statusId === AppointmentStatusCodes.Confirmed ||
          visit.statusId === AppointmentStatusCodes.Canceled) && (
          <VisitStatusOption status={visit.statusId} />
        )}
        {visit.statusId === AppointmentStatusCodes.Planned && (
          <UniLink
            icon={<SaveIcon />}
            title={i18next.t('status_confirmed')}
            color={ButtonColor.Secondary}
            className={styles.link}
            onClick={(event) => {
              event.stopPropagation();
              cancelOrConfirmVisit(AppointmentStatusCodes.Confirmed).catch(
                (e) => console.error('cancelOrConfirmVisit', e),
              );
            }}
          />
        )}
        {visit.statusId !== AppointmentStatusCodes.Canceled && (
          <UniLink
            icon={<CancelIcon />}
            title={i18next.t('status_canceled')}
            color={ButtonColor.Danger}
            className={styles.link}
            onClick={(event) => {
              event.stopPropagation();
              cancelOrConfirmVisit(AppointmentStatusCodes.Canceled).catch((e) =>
                console.error('cancelOrConfirmVisit', e),
              );
            }}
          />
        )}
      </div>
    </div>
  );
};
